import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { filter, switchMap } from 'rxjs/operators';
import { ConsentSendService } from 'src/app/core/api/consent-send.service';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { QuestionnaireSendBuilderService } from 'src/app/core/api/questionnaire-send-builder.service';
import { areAllIdsSelected, isExactIdMatch } from 'src/app/core/api/questionnaire-selection.util';
import { OneClickQRESetItem } from 'src/app/core/models/one-click-qre-set.model';
import { AccessState } from 'src/app/core/schemas/subscription-plan.schema';
import {
  OneClickQRESetError,
  OneClickQRESetService
} from 'src/app/core/services/one-click-qre-set.service';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { ConfirmModalService } from '../confirm-modal/confirm-modal.service';
import { OneClickQRESetNameDialogComponent } from '../one-click-qre-set-name-dialog/one-click-qre-set-name-dialog.component';
import {
  addControlsIfMissing,
  applyCompatibilityToFormGroup,
  collectSelectedSetItems,
  QuestionnaireLabelMaps,
  setAllFormGroupValues,
  setFormGroupSelectedIds
} from '../questionnaire-category-picker/questionnaire-selection-form';
import {
  OneClickQRESetChipView,
  PickerEntry,
  PickerSection,
  QuestionnairePickerSelection
} from './questionnaire-picker.model';

/**
 * The "what to send" half of a send-questionnaire flow: one-click templates, category filter chips
 * and the grouped checkbox list (standard questionnaires, custom questionnaires, intake forms,
 * consent forms and — when the host opts in — Patient App todos).
 *
 * The host keeps its own review pane and action bar; this component owns the selection state and
 * reports it through `selectionChange`.
 */
@Component({
  selector: 'csi-questionnaire-picker',
  templateUrl: './questionnaire-picker.component.html',
  styleUrls: ['./questionnaire-picker.component.scss']
})
export class QuestionnairePickerComponent implements OnInit, OnChanges, OnDestroy {
  /** Boolean control per questionnaire/custom/intake/consent key. Owned by the host. */
  @Input() questionnaireTypesFormGroup: FormGroup;
  /** Boolean control per Patient App todo id. Only read when `includePampTodos` is true. */
  @Input() pampTodosFormGroup: FormGroup = new FormGroup({});
  @Input() pampTodoKeyToFriendlyNameMap: { [key: string]: string } = {};
  /** Patient App todos need a per-patient enrollment code, so multi-patient hosts opt out. */
  @Input() includePampTodos = true;
  /**
   * The host has actually loaded Patient App todos worth showing. Hosts must only set this once
   * `AppStateService.getIsPatientAppActive()` holds — the picker has no app state of its own, and
   * both current hosts enforce it by refusing to fetch the todos at all when the app is inactive.
   */
  @Input() showPatientAppCategory = false;
  @Input() sendIntakeFormsEnabled = true;
  /** Keys ticked when the picker first builds (e.g. resending a declined consent). */
  @Input() preselectedKeys: string[] = [];
  /** Key -> reason. Blocked entries render disabled, and templates bundling one cannot be applied. */
  @Input() blockedEntryKeys: { [key: string]: string } = {};

  @Output() selectionChange = new EventEmitter<QuestionnairePickerSelection>();

  /** Every group of selectable items, in the order their filter chips are shown. */
  public sections: PickerSection[] = [];
  /** Sentinel for the "All" chip. Public so the template can compare against it. */
  public readonly allFilterId = 'all';
  /** The one active filter chip: a section id, or `allFilterId` for the unfiltered list. */
  public activeFilterId = this.allFilterId;
  /** Sections passing the current chip filter - drives the checkbox list. */
  public visibleSections: PickerSection[] = [];
  /** Group headings only earn their space once the list mixes more than one group. */
  public showGroupTitles = false;
  /** The number on the "All" chip. */
  public totalEntryCount = 0;

  public oneClickQRESetChips: OneClickQRESetChipView[] = [];
  public canManageOneClickQRESets = false;
  public saveOneClickQRESetDisabled = true;
  public multiSendDisabled = false;

  public questionnaireTypeToFriendlyNameMap: { [key: string]: string } = {};
  public intakeFormKeyToFriendlyNameMap: { [key: string]: string } = {};
  public consentFormKeyToFriendlyNameMap: { [key: string]: string } = {};

  private selectedItems: PickerEntry[] = [];
  private hasBuiltSections = false;
  /** Keys this component disabled because they were blocked, so it only re-enables its own. */
  private keysDisabledByBlocking: { [key: string]: boolean } = {};
  /**
   * Set while a lifecycle hook is rebuilding. The host's bindings have already been checked for
   * this cycle, so emitting from inside ngOnInit/ngOnChanges would mutate them mid-check; those
   * paths emit once on the next microtask instead.
   */
  private isRebuilding = false;

  constructor(
    public questionnaireRequestService: QuestionnaireRequestService,
    private questionnaireSendBuilderService: QuestionnaireSendBuilderService,
    private consentSendService: ConsentSendService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService,
    private oneClickQRESetService: OneClickQRESetService,
    private confirmModalService: ConfirmModalService,
    private clinicSetupService: ClinicSetupService,
    private translocoService: TranslocoService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog
  ) {
    const { CUSTOM, ...rest } = this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap;
    this.questionnaireTypeToFriendlyNameMap = rest as { [key: string]: string };
    this.canManageOneClickQRESets = this.oneClickQRESetService.canMutateAllSets();
  }

  /** Required by untilDestroyed(this), which hooks this method to complete its subscriptions. */
  ngOnDestroy() {}

  ngOnInit() {
    this.rebuild(() => this.applyPreselectedKeys());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.hasBuiltSections) {
      return;
    }
    // Patient App todos arrive asynchronously and the intake/blocked inputs can change once the
    // host resolves them, so rebuild rather than leaving a stale list on screen.
    if (
      changes.pampTodoKeyToFriendlyNameMap ||
      changes.showPatientAppCategory ||
      changes.includePampTodos ||
      changes.sendIntakeFormsEnabled ||
      changes.blockedEntryKeys
    ) {
      this.rebuild(() => this.refreshSelectionState());
    }
  }

  /** Rebuilds the sections, then hands the host one selection on the next microtask. */
  private rebuild(afterBuild: () => void) {
    this.isRebuilding = true;
    this.initializeNameMaps();
    this.buildSections();
    afterBuild();
    this.isRebuilding = false;
    Promise.resolve().then(() => this.emitSelection());
  }

  private initializeNameMaps() {
    this.intakeFormKeyToFriendlyNameMap = Object.entries(
      this.questionnaireSendBuilderService.getIntakeFormKeyToSchemaMap()
    ).reduce((keyToName, [key, schema]) => {
      keyToName[key] = schema['name'];
      return keyToName;
    }, {});
    this.consentFormKeyToFriendlyNameMap = this.consentSendService.getConsentFormKeyToFriendlyNameMap();
  }

  /* --- Sections ---------------------------------------------------------- */

  private buildSections() {
    const customQuestionnaireTemplates = this.clinicSetupService.formTemplateTypeToTemplatesMap[
      'questionnaire'
    ];

    this.sections = [
      this.createSection(
        'questionnaires',
        'Questionnaires',
        'Questionnaires',
        this.questionnaireTypeToFriendlyNameMap,
        'questionnaires'
      )
    ];

    if (customQuestionnaireTemplates && customQuestionnaireTemplates.length > 0) {
      const customSection = this.createSection(
        'custom',
        'Custom Questionnaire(s)',
        'Custom',
        this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap
      );
      customSection.panelAccessKey = 'questionnaires.CUSTOM';
      this.sections.push(customSection);
    }

    if (this.sendIntakeFormsEnabled) {
      this.sections.push(
        this.createSection(
          'intakeForms',
          'Intake Form(s)',
          'Intake',
          this.intakeFormKeyToFriendlyNameMap,
          'intakeForms'
        )
      );
    }

    this.sections.push(
      this.createSection(
        'consentForms',
        'Consent Form(s)',
        'Consent',
        this.consentFormKeyToFriendlyNameMap,
        'consentForms'
      )
    );

    if (this.includePampTodos && this.showPatientAppCategory) {
      this.sections.push(
        this.createSection(
          'pampTodos',
          'Patient App Test(s) (Delivered via Patient App)',
          'Patient App',
          this.pampTodoKeyToFriendlyNameMap,
          '',
          this.pampTodosFormGroup
        )
      );
    }

    this.sections = this.sections.filter(
      section => section.entries.length > 0 && !this.isSectionHidden(section)
    );
    this.totalEntryCount = this.sections.reduce(
      (total, section) => total + section.entries.length,
      0
    );
    this.updateVisibleSections();
    this.applyBlockedEntries();
    this.hasBuiltSections = true;
  }

  private createSection(
    id: string,
    title: string,
    chipLabel: string,
    keyToNameMap: { [key: string]: string },
    accessKeyPrefix?: string,
    targetFormGroup?: FormGroup
  ): PickerSection {
    const formGroup = targetFormGroup || this.questionnaireTypesFormGroup;
    const isPampTodo = formGroup === this.pampTodosFormGroup;
    const keys = Object.keys(keyToNameMap || {});
    addControlsIfMissing(formGroup, keys);

    const entries = keys
      .filter(key => !accessKeyPrefix || !this.isAccessKeyHidden(accessKeyPrefix + '.' + key))
      .map(key => ({
        key,
        name: keyToNameMap[key],
        accessKey: accessKeyPrefix ? accessKeyPrefix + '.' + key : '',
        isPampTodo,
        isBlocked: !!this.blockedEntryKeys[key],
        blockedReason: this.blockedEntryKeys[key] || ''
      }));

    return { id, title, chipLabel, formGroup, entries };
  }

  /**
   * Blocked entries are cleared and disabled at the control level: a reactive-forms checkbox cannot
   * be disabled from the template, and clearing them keeps them out of every selection read.
   *
   * Controls outlive a rebuild, so an entry blocked on an earlier cycle has to be actively enabled
   * again once the host drops its key. Only the keys blocking itself disabled are re-enabled --
   * compatibility disables controls too, and this pass runs right after it.
   */
  private applyBlockedEntries() {
    this.sections.forEach(section => {
      section.entries.forEach(entry => {
        const control = section.formGroup.controls[entry.key];
        if (!control) {
          return;
        }
        if (entry.isBlocked) {
          control.setValue(false, { emitEvent: false });
          control.disable({ emitEvent: false });
          this.keysDisabledByBlocking[entry.key] = true;
          return;
        }
        if (this.keysDisabledByBlocking[entry.key]) {
          delete this.keysDisabledByBlocking[entry.key];
          control.enable({ emitEvent: false });
        }
      });
    });
  }

  private isAccessKeyHidden(key: string): boolean {
    const accessConfig = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema[key];
    return !!accessConfig && accessConfig.accessState === AccessState.HIDDEN;
  }

  private isSectionHidden(section: PickerSection): boolean {
    return !!section.panelAccessKey && this.isAccessKeyHidden(section.panelAccessKey);
  }

  // Ticks keys the host asked for and runs them through the normal selection path, so
  // compatibility rules apply as if the user clicked. Only keys with a visible, unblocked picker
  // entry qualify: a plan-hidden or blocked form must never be sent unseen.
  private applyPreselectedKeys() {
    const selectableKeys = this.sections
      .filter(section => section.formGroup === this.questionnaireTypesFormGroup)
      .reduce(
        (keys: string[], section) =>
          keys.concat(section.entries.filter(entry => !entry.isBlocked).map(entry => entry.key)),
        []
      );
    const preselectedKeys = (this.preselectedKeys || []).filter(
      key => selectableKeys.indexOf(key) > -1
    );
    preselectedKeys.forEach(key => this.questionnaireTypesFormGroup.controls[key].setValue(true));
    if (preselectedKeys.length > 0) {
      this.applyCompatibility();
    }
    this.refreshSelectionState();
  }

  /* --- Filter chips ------------------------------------------------------ */

  public onAllFilterClick() {
    this.activeFilterId = this.allFilterId;
    this.updateVisibleSections();
  }

  public onSectionFilterClick(section: PickerSection) {
    this.activeFilterId = this.activeFilterId === section.id ? this.allFilterId : section.id;
    this.updateVisibleSections();
  }

  private updateVisibleSections() {
    this.visibleSections =
      this.activeFilterId === this.allFilterId
        ? this.sections
        : this.sections.filter(section => section.id === this.activeFilterId);
    this.showGroupTitles = this.visibleSections.length > 1;
  }

  public trackBySectionId(_index: number, section: PickerSection): string {
    return section.id;
  }

  public trackByEntryKey(_index: number, entry: PickerEntry): string {
    return entry.key;
  }

  public trackBySetId(_index: number, setChip: OneClickQRESetChipView): string {
    return setChip.id;
  }

  /* --- Selection --------------------------------------------------------- */

  public onQuestionnaireSelectChange() {
    this.applyCompatibility();
    this.refreshSelectionState();
  }

  /** PAMP todo ids are never part of the questionnaire exclusivity rules, so skip that pass. */
  public onPampItemChange() {
    this.refreshSelectionState();
  }

  public deselect(entry: PickerEntry) {
    const formGroup = entry.isPampTodo ? this.pampTodosFormGroup : this.questionnaireTypesFormGroup;
    const control = formGroup.controls[entry.key];
    if (!control) {
      return;
    }
    control.setValue(false);
    if (entry.isPampTodo) {
      this.refreshSelectionState();
      return;
    }
    this.onQuestionnaireSelectChange();
  }

  public clearAllSelections() {
    if (this.questionnaireTypesFormGroup) {
      setAllFormGroupValues(this.questionnaireTypesFormGroup, false);
      this.applyCompatibility();
    }
    setAllFormGroupValues(this.pampTodosFormGroup, false);
    this.refreshSelectionState();
  }

  /** Compatibility re-enables the toggle questionnaires, so blocking has to be re-applied after. */
  private applyCompatibility(preferredIds?: string[]) {
    this.multiSendDisabled = applyCompatibilityToFormGroup(
      this.questionnaireTypesFormGroup,
      this.sendIntakeFormsEnabled,
      preferredIds
    );
    this.applyBlockedEntries();
  }

  private refreshSelectionState() {
    this.refreshSelection();
    this.refreshOneClickQRESetChips();
    this.updateSaveOneClickQRESetDisabled();
    this.emitSelection();
  }

  private refreshSelection() {
    const selectedItems: PickerEntry[] = [];
    this.sections.forEach(section => {
      section.entries.forEach(entry => {
        const control = section.formGroup.controls[entry.key];
        if (control && control.value === true) {
          selectedItems.push(entry);
        }
      });
    });
    this.selectedItems = selectedItems;
  }

  private emitSelection() {
    if (this.isRebuilding) {
      return;
    }
    this.selectionChange.emit({
      selectedItems: this.selectedItems,
      multiSendDisabled: this.multiSendDisabled,
      atLeastOneQuestionnaireSelected: this.selectedItems.some(entry => !entry.isPampTodo),
      atLeastOneItemSelected: this.selectedItems.length > 0,
      canManageOneClickQRESets: this.canManageOneClickQRESets,
      saveOneClickQRESetDisabled: this.saveOneClickQRESetDisabled
    });
  }

  /* --- One-click QRE set templates --------------------------------------- */

  public applyOneClickQRESet(setChip: OneClickQRESetChipView) {
    if (!setChip || !setChip.source || setChip.isBlocked) {
      return;
    }
    const availableIds = this.buildAvailableIdMap();
    const resolved = this.oneClickQRESetService.resolveSetItems(setChip.source, availableIds);
    const availableItemIds = resolved.availableItems.map(item => item.id);
    if (availableItemIds.length === 0) {
      this.showMissingSetItems(resolved.missingItems);
      return;
    }

    // Templates are presets, not additive: applying one replaces the whole selection, and
    // clicking the applied template clears it. setFormGroupSelectedIds writes every control,
    // so passing just the template's ids does the clearing implicitly.
    const isClearing = setChip.isActive;
    const nextIds = isClearing ? [] : availableItemIds;

    const questionnaireSelected: { [id: string]: boolean } = {};
    const pampSelected: { [id: string]: boolean } = {};
    nextIds.forEach(id => {
      if (this.pampTodosFormGroup && this.pampTodosFormGroup.controls[id]) {
        pampSelected[id] = true;
      } else {
        questionnaireSelected[id] = true;
      }
    });

    if (this.questionnaireTypesFormGroup) {
      setFormGroupSelectedIds(this.questionnaireTypesFormGroup, questionnaireSelected);
      this.applyCompatibility(nextIds);
    }
    setFormGroupSelectedIds(this.pampTodosFormGroup, pampSelected);
    this.refreshSelectionState();

    if (!isClearing) {
      const notices: string[] = [];
      if (resolved.missingItems && resolved.missingItems.length > 0) {
        notices.push(
          this.translocoService.translate('Some items in this template are no longer available') +
            ': ' +
            resolved.missingItems.map(item => item.label).join(', ')
        );
      }
      if (!areAllIdsSelected(this.allSelectedIds(), availableItemIds)) {
        notices.push(
          this.translocoService.translate(
            'Some questionnaires from this template could not be combined'
          )
        );
      }
      if (notices.length > 0) {
        this.snackBar.open(notices.join('. '), '', { duration: 4000 });
      }
    }
  }

  private showMissingSetItems(missingItems: OneClickQRESetItem[]) {
    if (!missingItems || missingItems.length === 0) {
      return;
    }
    const missingLabels = missingItems.map(item => item.label).join(', ');
    this.snackBar.open(
      this.translocoService.translate('Some items in this template are no longer available') +
        ': ' +
        missingLabels,
      '',
      { duration: 4000 }
    );
  }

  public saveOneClickQRESet() {
    const items = this.collectSelectedItems();
    if (items.length === 0 || this.saveOneClickQRESetDisabled) {
      return;
    }
    OneClickQRESetNameDialogComponent.open(this.matDialog, {
      title: 'Save as Template',
      confirmLabel: 'Save',
      allowExistingName: true
    })
      .afterClosed()
      .pipe(
        filter((name: string) => !!name),
        switchMap((name: string) =>
          this.oneClickQRESetService.createOrOverwriteSet(name, items, this.sendIntakeFormsEnabled)
        ),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshOneClickQRESetChips();
          this.snackBar.open(this.translocoService.translate('Template saved'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.snackBar.open(
            this.translocoService.translate(error.errorKey || 'Unable to save template'),
            '',
            { duration: 4000 }
          );
        }
      );
  }

  public renameOneClickQRESet(setChip: OneClickQRESetChipView) {
    if (!setChip || !setChip.canMutate) {
      return;
    }
    OneClickQRESetNameDialogComponent.open(this.matDialog, {
      title: 'Rename Template',
      confirmLabel: 'Save',
      initialName: setChip.name,
      excludeSetId: setChip.id
    })
      .afterClosed()
      .pipe(
        filter((name: string) => !!name),
        switchMap((name: string) =>
          this.oneClickQRESetService.updateSet(setChip.id, { name }, this.sendIntakeFormsEnabled)
        ),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshOneClickQRESetChips();
          this.snackBar.open(this.translocoService.translate('Template saved'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.snackBar.open(
            this.translocoService.translate(error.errorKey || 'Unable to save template'),
            '',
            { duration: 4000 }
          );
        }
      );
  }

  public deleteOneClickQRESet(setChip: OneClickQRESetChipView) {
    if (!setChip || !setChip.canMutate) {
      return;
    }
    this.confirmModalService
      .show(
        this.translocoService.translate('Delete Template'),
        this.translocoService.translate('Delete this template?'),
        this.translocoService.translate('Delete'),
        this.translocoService.translate('Cancel')
      )
      .pipe(
        filter(confirmed => !!confirmed),
        switchMap(() => this.oneClickQRESetService.deleteSet(setChip.id)),
        untilDestroyed(this)
      )
      .subscribe(
        () => {
          this.refreshOneClickQRESetChips();
          this.snackBar.open(this.translocoService.translate('Template deleted'), '', {
            duration: 3000
          });
        },
        (error: OneClickQRESetError) => {
          this.snackBar.open(
            this.translocoService.translate(error.errorKey || 'Unable to delete template'),
            '',
            { duration: 4000 }
          );
        }
      );
  }

  private refreshOneClickQRESetChips() {
    const selectedIds = this.allSelectedIds();
    const availableIds = this.buildAvailableIdMap();
    this.oneClickQRESetChips = this.oneClickQRESetService.getSets().map(set => {
      const resolved = this.oneClickQRESetService.resolveSetItems(set, availableIds);
      const requiredIds = resolved.availableItems.map(item => item.id);
      const blockedItems = set.items.filter(item => !!this.blockedEntryKeys[item.id]);
      return {
        id: set.id,
        name: set.name,
        canMutate: this.oneClickQRESetService.canMutateSet(set),
        // Exact match, not subset: a nested template (or one whose items got trimmed by
        // resolveSetItems) would otherwise light up alongside the one actually applied.
        isActive: isExactIdMatch(selectedIds, requiredIds),
        itemSummary: set.items.map(item => item.label).join(', '),
        isBlocked: blockedItems.length > 0,
        blockedReason: this.buildBlockedSetReason(blockedItems),
        source: set
      };
    });
  }

  private buildBlockedSetReason(blockedItems: OneClickQRESetItem[]): string {
    if (blockedItems.length === 0) {
      return '';
    }
    return (
      this.translocoService.translate('This template cannot be used here') +
      ': ' +
      blockedItems.map(item => item.label).join(', ')
    );
  }

  private updateSaveOneClickQRESetDisabled() {
    this.saveOneClickQRESetDisabled = !this.oneClickQRESetService.canSaveSet(
      this.collectSelectedItems().length
    );
  }

  private collectSelectedItems(): OneClickQRESetItem[] {
    return collectSelectedSetItems(
      this.questionnaireTypesFormGroup,
      this.pampTodosFormGroup,
      this.questionnaireLabelMaps()
    );
  }

  private questionnaireLabelMaps(): QuestionnaireLabelMaps {
    return {
      standardKeyToName: this.questionnaireTypeToFriendlyNameMap || {},
      customKeyToName:
        this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap || {},
      consentKeyToName: this.consentFormKeyToFriendlyNameMap || {},
      intakeKeyToName: this.intakeFormKeyToFriendlyNameMap || {},
      pampTodoKeyToName: this.pampTodoKeyToFriendlyNameMap || {}
    };
  }

  private allSelectedIds(): string[] {
    return this.collectSelectedItems().map(item => item.id);
  }

  private buildAvailableIdMap(): { [id: string]: boolean } {
    const availableIds: { [id: string]: boolean } = {};
    if (this.questionnaireTypesFormGroup) {
      Object.keys(this.questionnaireTypesFormGroup.controls).forEach(id => {
        availableIds[id] = true;
      });
    }
    Object.keys(this.pampTodosFormGroup.controls).forEach(id => {
      availableIds[id] = true;
    });
    return availableIds;
  }
}
